import {MechanicValidator} from './MechanicValidator.ts'
import {CardValidatorOrdered} from '@colonist/game-mechanic-validators'
import {type ActionStatus, PieceTypes} from '@colonist/game-base-types'
import {getCardsForPiece} from '@colonist/game-card-types'
import {PlayerColor, type MechanicDevelopmentCardsState, CardEnum, type NestedReadonly, ActionState} from '@colonist/game-states'
import {ArrayUtils} from '@colonist/utils'
import {MechanicValidatorDevelopmentCardPlayer} from './MechanicValidatorDevelopmentCardPlayer.ts'
import {type PlayerValidator} from '../core/PlayerValidator.ts'
import {type MapValidator} from '../core/map/MapValidator.ts'
import {type CurrentStateValidator} from '../core/CurrentStateValidator.ts'

export class MechanicValidatorDevelopmentCard extends MechanicValidator {
    private readonly bankDevelopmentCards: CardValidatorOrdered
    private readonly playerDevelopmentCardStates: Map<PlayerColor, MechanicValidatorDevelopmentCardPlayer>
    private readonly currentStateValidator: CurrentStateValidator

    constructor(playerValidators: PlayerValidator[], mapValidator: MapValidator, currentStateValidator: CurrentStateValidator, developmentCardsState: NestedReadonly<MechanicDevelopmentCardsState>) {
        super(playerValidators, mapValidator)

        this.currentStateValidator = currentStateValidator
        this.bankDevelopmentCards = new CardValidatorOrdered(developmentCardsState.bankDevelopmentCards)

        this.playerDevelopmentCardStates = new Map()
        for(const player in developmentCardsState.players) {
            const playerColor = Number(player) as PlayerColor
            const playerDevelopmentCardState = developmentCardsState.players[playerColor]

            if(playerDevelopmentCardState == undefined) continue

            this.playerDevelopmentCardStates.set(playerColor, new MechanicValidatorDevelopmentCardPlayer(playerDevelopmentCardState))
        }
        this.playerDevelopmentCardStates.set(PlayerColor.None, new MechanicValidatorDevelopmentCardPlayer({
            developmentCards: {cards: []},
            developmentCardsUsed: [],
        }))
    }

    getDevelopmentCardsPlayerState(player: PlayerColor): MechanicValidatorDevelopmentCardPlayer {
        const devCardsPlayerState = this.playerDevelopmentCardStates.get(player)
        if(devCardsPlayerState === undefined) throw new Error(`No MechanicDevelopmentCardsPlayerState for player ${player}`)
        return devCardsPlayerState
    }

    hasResourcesForDevelopmentCard(player: PlayerColor): ActionStatus {
        const playerController = this.playerValidators.get(player)
        if(playerController == undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noPlayer'}}

        const resourcesForDevelopmentCard = getCardsForPiece(PieceTypes.DevelopmentCard)
        const hasResourcesForDevelopmentCard = playerController.hasResourceCardsInHand(resourcesForDevelopmentCard)
        if(!hasResourcesForDevelopmentCard) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    private isOpponentInBlockingState(card: CardEnum, player: PlayerColor): boolean {
        switch(card) {
            case CardEnum.Knight: return this.currentStateValidator.isOpponentInKnightBlockingState(player)
            default: return false
        }
    }

    canPlayerUseDevelopmentCard(player: PlayerColor, card: CardEnum): ActionStatus {
        const isSomePlayerDiscarding = this.currentStateValidator.isSomePlayerInActionState(ActionState.SelectCardsToDiscard)
        if(isSomePlayerDiscarding) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.playersDiscarding'}}

        if(this.isOpponentInBlockingState(card, player)) return MechanicValidatorDevelopmentCard.getErrorMessageIfBlockedByOpponentActionState(card)

        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player)

        const developmentCards = new CardValidatorOrdered(devCardsPlayerState.developmentCards)
        const hasCard = developmentCards.cards.includes(card)
        if(hasCard === false) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.developmentCardNotInHand'}}

        const isVictoryPoint = card == CardEnum.VictoryPoint
        if(isVictoryPoint) return {canTakeAction: true}

        if(devCardsPlayerState.hasUsedDevelopmentCardThisTurn) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.gameState.gameLogs.canOnlyPlayOneDevCardPerTurn'}}

        const currentCardCount = developmentCards.getCardCountForCard(card)
        const amountPurchasedThisTurn = ArrayUtils.countOccurenceOfValueInArray(devCardsPlayerState.developmentCardsBoughtThisTurn ?? [], card)
        if(currentCardCount <= amountPurchasedThisTurn) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.gameState.gameLogs.cannotPlayDevCardOnTurnYouBoughtIt'}}

        return {canTakeAction: true}
    }

    canPlayerBuyDevelopmentCard(player: PlayerColor): ActionStatus {
        const isSomePlayerDiscarding = this.currentStateValidator.isSomePlayerInActionState(ActionState.SelectCardsToDiscard)
        if(isSomePlayerDiscarding) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.playersDiscarding'}}

        const isDevelopmentCardAvailableInBank = this.bankDevelopmentCards.cards.length > 0
        if(!isDevelopmentCardAvailableInBank) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noDevelopmentCardsInBank'}}

        const hasResourcesForDevelopmentCard = this.hasResourcesForDevelopmentCard(player).canTakeAction
        if(!hasResourcesForDevelopmentCard) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    canPlayerUseAtLeastOneDevelopmentCard(player: PlayerColor): boolean {
        for(const card of this.getDevelopmentCardsPlayerState(player).developmentCards.cards) {
            if(card == CardEnum.VictoryPoint) continue
            if(!this.canPlayerUseDevelopmentCard(player, card).canTakeAction) continue
            return true
        }
        return false
    }

    getPlayersTotalDevelopmentCards(player: PlayerColor): number {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player)
        return devCardsPlayerState.developmentCards.cards.length
    }

    getPlayersTotalDevelopmentCardsOfType(player: PlayerColor, cardEnum: CardEnum): number {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player)
        const developmentCards = new CardValidatorOrdered(devCardsPlayerState.developmentCards)
        return ArrayUtils.countOccurenceOfValueInArray(developmentCards.cards, cardEnum)
    }

    getPlayersTotalUsedDevelopmentCardsOfType(player: PlayerColor, cardEnum: CardEnum) {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player)
        return ArrayUtils.countOccurenceOfValueInArray(devCardsPlayerState.developmentCardsUsed, cardEnum)
    }

    getTotalDevelopmentCardsInBank(): number {
        return this.bankDevelopmentCards.getNumberOfCardsInInventory()
    }

    private static getErrorMessageIfBlockedByOpponentActionState(card: CardEnum): ActionStatus {
        switch(card) {
            case CardEnum.Knight: return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.knightUsageBlockedByOpponentAction'}}
            default: return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.developmentCardBlockedByOpponentAction'}}
        }
    }
}
